/* The merchant dashboard.
 *
 * Built 2026-09-17 from `Damga Merchant Dashboard v2.dc.html`, against the
 * reading in reports/surfaces/merchant-dashboard.md: the sidebar and the screens
 * the product can answer, each with the empty state it will show until a shop
 * has customers, and nothing that describes a product that does not exist.
 *
 * ONE BUSINESS, THE ONE THIS ACCOUNT OWNS. `/me/business` answers with it, and
 * every read is scoped to it by the API's own ownership check. A merchant with
 * two shops is a case the API does not have yet and this screen does not invent.
 *
 * ONE WRITE, AND EVERYTHING ELSE IS A GET. The things the artboard lets a
 * merchant change -- the card's rule, the team, the branches -- are changes with
 * consequences for cards already in customers' hands, and they are done with us
 * until there is a reason to make them self-serve. The screens say that rather
 * than showing a disabled button.
 *
 * The exception, added 2026-09-17, is bringing paper cards across. It is a
 * different act: a shop declaring the customers it already has, not an edit to
 * a card somebody is holding. It is additive, it is attributed to the account
 * that declared it, both conflict rules favour the customer, and anything large
 * or generous enough to be worth questioning is held for a person before it
 * lands. The alternative was a merchant emailing a spreadsheet for somebody here
 * to type in, which is slower and attributed to the wrong person.
 *
 * 2026-09-18, BY THE FOUNDER: A PASSWORD DAMGA ISSUES, AND ROLES THAT ARE REAL.
 * A merchant signs in with the username Damga gave them and a password they set
 * from a link; Google, Microsoft and the emailed code were the customer's doors
 * and are gone from here. The owner sees everything, the counter included, and
 * adds, re-links and removes the people on the till and starts the shop's card.
 * Anyone else on the team signs in to the counter and nothing else -- and not
 * because this screen hides the rest: the API refuses them every other route, and
 * this screen does not even ask.
 */
import { MerchantSetup, MerchantSignIn, takeSetupToken } from "@damga/ds";
import {
  Forbidden,
  Offline,
  SessionExpired,
  type Session,
  createClient,
  createMerchantAuth,
  isRefusal,
  merchantRefreshUrl,
  readSession,
  writeSession,
} from "@damga/kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { t } from "../content";
import { API_URL, AUTH_URL } from "./config";
import { CounterScreen } from "./CounterScreen";
import {
  BranchesScreen,
  CustomersScreen,
  CardScreen,
  ExportsScreen,
  FIRST_CARD_GAP_MINUTES,
  ImportScreen,
  type Branch,
  type Customer,
  type ImportPlan,
  type ImportRow,
  type Overview,
  type Program,
  type Reward,
  type Staff,
  type Till,
  OverviewScreen,
  ProgrammeScreen,
  RewardsScreen,
  SettingsScreen,
  TeamScreen,
  type TeamActions,
} from "./screens";
import { setupWords, signInWords } from "./words";

type Tab = "overview" | "counter" | "customers" | "programme" | "rewards" | "branches" | "team" | "import" | "card" | "reports" | "settings";
const TABS: { key: Tab; label: Parameters<typeof t>[0] }[] = [
  { key: "overview", label: "nav.overview" },
  { key: "counter", label: "nav.counter" },
  { key: "customers", label: "nav.customers" },
  { key: "programme", label: "nav.programme" },
  { key: "rewards", label: "nav.rewards" },
  { key: "branches", label: "nav.branches" },
  { key: "team", label: "nav.team" },
  { key: "import", label: "nav.import" },
  { key: "card", label: "nav.card" },
  { key: "reports", label: "nav.reports" },
  { key: "settings", label: "nav.settings" },
];

type Business = {
  id: string;
  name: string;
  /** "owner", or anything else on the team: the counter and nothing more. */
  my_role: string;
  // 048. Null for a shop enrolled before the column existed.
  contact_phone?: string | null;
  city?: string | null;
};

/** A spreadsheet's worth of what is on screen, made in the browser. */
function csv(rows: Record<string, unknown>[], name: string) {
  if (rows.length === 0) return;
  const cols = Object.keys(rows[0]!);
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `damga-${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function App() {
  const [session, setSessionState] = useState<Session | null>(() => readSession<Session>());
  // A setup link: read from the fragment once, and the address bar cleared.
  const [setupToken, setSetupToken] = useState<string | null>(() => takeSetupToken());
  const [prefill, setPrefill] = useState("");
  const [business, setBusiness] = useState<Business | null>(null);
  const [problem, setProblem] = useState<"offline" | "server" | "notStaff" | null>(null);
  // The narrow screens' menu (see the sidebar below).
  const [menuOpen, setMenuOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("overview");
  const [days, setDays] = useState(30);
  const [query, setQuery] = useState("");

  const [overview, setOverview] = useState<Overview | null>(null);
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [rewards, setRewards] = useState<Reward[] | null>(null);
  const [tills, setTills] = useState<Till[] | null>(null);
  const [program, setProgram] = useState<Program>(null);
  const [asked, setAsked] = useState(false);

  const setSession = useCallback((s: Session | null) => {
    writeSession(s);
    setSessionState(s);
    if (!s) {
      setBusiness(null);
      setAsked(false);
      setTab("overview");
    }
  }, []);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const api = useMemo(
    () =>
      createClient({
        apiUrl: API_URL,
        authUrl: AUTH_URL,
        // A merchant session is the API's own; it refreshes there, not at GoTrue.
        refreshUrl: merchantRefreshUrl(API_URL),
        getSession: () => sessionRef.current,
        setSession,
      }),
    [setSession],
  );
  const merchantAuth = useMemo(() => createMerchantAuth(API_URL), []);

  const signOut = useCallback(() => {
    const current = sessionRef.current;
    if (current) void merchantAuth.signOut(current.refresh_token);
    setSession(null);
  }, [merchantAuth, setSession]);

  /* One load for the shop, then one per screen's data. Every call is scoped to
   * the business by the API; the id travels in the query because that is the
   * shape the merchant family already uses. */
  const load = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const biz = await api.call<Business>("/me/business");
      if (isRefusal(biz)) {
        // The account is signed in and on no shop's team. That is a real state
        // for somebody Damga has not added yet, and it is not an error screen.
        setProblem("notStaff");
        setAsked(true);
        return;
      }
      const b = biz as unknown as Business;
      setBusiness(b);
      setProblem(null);
      // THE COUNTER AND NOTHING ELSE for anyone but the owner. Not asked for,
      // rather than asked for and refused: the API would refuse every one of
      // these (E_OWNER_REQUIRED), and a till tablet has no reason to try.
      if (b.my_role !== "owner") {
        setTab("counter");
        setAsked(true);
        return;
      }
      const q = `business_id=${b.id}`;
      const [ov, cu, br, st, rw, pr, ti] = await Promise.all([
        api.call<{ [k: string]: unknown }>(`/merchant/overview?${q}&days=${days}`),
        api.call<{ customers: Customer[] }>(`/merchant/customers?${q}`),
        api.call<{ branches: Branch[] }>(`/merchant/branches?${q}`),
        api.call<{ staff: Staff[] }>(`/merchant/staff?${q}`),
        api.call<{ rewards: Reward[] }>(`/merchant/rewards?${q}&days=${days}`),
        api.call<{ program: Program }>(`/merchant/program?${q}`),
        // Only the readiness list reads this, and only until the first stamp.
        api.call<{ tills: Till[] }>(`/merchant/tills?${q}`),
      ]);
      if (!isRefusal(ov)) setOverview(ov as unknown as Overview);
      if (!isRefusal(cu)) setCustomers((cu as { customers: Customer[] }).customers);
      if (!isRefusal(br)) setBranches((br as { branches: Branch[] }).branches);
      if (!isRefusal(st)) setStaff((st as { staff: Staff[] }).staff);
      if (!isRefusal(rw)) setRewards((rw as { rewards: Reward[] }).rewards);
      if (!isRefusal(pr)) setProgram((pr as { program: Program }).program);
      if (!isRefusal(ti)) setTills((ti as { tills: Till[] }).tills);
      setAsked(true);
    } catch (err) {
      setAsked(true);
      if (err instanceof SessionExpired) setSession(null);
      // A session from before 2026-09-18 came in by the customer's door, which
      // no longer opens the shop: sign in again, by the merchant's.
      else if (err instanceof Forbidden && err.code === "E_MERCHANT_SIGN_IN") setSession(null);
      else if (err instanceof Offline) setProblem("offline");
      else setProblem("server");
    }
  }, [api, days, setSession]);

  useEffect(() => {
    void load();
  }, [load, session]);

  /* Bringing paper cards across. The rows live here until the merchant asks
   * what they will do; the batch and its plan live here after that. Nothing is
   * kept between visits deliberately: a half-typed import restored from
   * storage a week later is a list nobody remembers checking. */
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importBatch, setImportBatch] = useState<string | null>(null);
  const [importPlan, setImportPlan] = useState<ImportPlan | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importDone, setImportDone] = useState<number | null>(null);
  const [importProblem, setImportProblem] = useState<string | null>(null);

  const startOver = useCallback(() => {
    setImportRows([]);
    setImportBatch(null);
    setImportPlan(null);
    setImportDone(null);
    setImportProblem(null);
  }, []);

  const addImportRow = useCallback((row: ImportRow): string | null => {
    let complaint: string | null = null;
    setImportRows((rows) => {
      if (rows.some((r) => r.phone === row.phone)) {
        // The API would keep the higher of the two, which is right for a
        // spreadsheet and wrong for a typo made ten seconds ago.
        complaint = t("import.duplicate");
        return rows;
      }
      return [...rows, row];
    });
    return complaint;
  }, []);

  const checkImport = useCallback(async () => {
    if (!business || importRows.length === 0) return;
    setImportBusy(true);
    setImportProblem(null);
    try {
      const created = await api.call<{ batch_id: string }>("/merchant/imports", {
        method: "POST",
        body: {
          business_id: business.id,
          program_id: program?.id,
          source: "typed",
          rows: importRows.map((r) => ({
            phone_e164: r.phone,
            opening_balance: r.stamps,
            declared_name: r.name || null,
          })),
        },
      });
      if (isRefusal(created)) {
        setImportProblem(t("import.problem"));
        return;
      }
      const batch = (created as { batch_id: string }).batch_id;
      setImportBatch(batch);
      const plan = await api.call<ImportPlan>(`/merchant/imports/${batch}/plan`);
      if (isRefusal(plan)) setImportProblem(t("import.problem"));
      else setImportPlan(plan as unknown as ImportPlan);
    } catch {
      setImportProblem(t("import.problem"));
    } finally {
      setImportBusy(false);
    }
  }, [api, business, importRows, program]);

  const applyImport = useCallback(async () => {
    if (!importBatch) return;
    setImportBusy(true);
    setImportProblem(null);
    try {
      const r = await api.call<{ status: number }>(`/merchant/imports/${importBatch}/apply`, {
        method: "POST",
      });
      if (isRefusal(r) || (r as { status: number }).status !== 200) {
        setImportProblem(t("import.problem"));
        return;
      }
      setImportDone(importPlan?.unique_members ?? importRows.length);
      // What the shop now holds has changed, so the rest of the dashboard is
      // out of date until it is read again.
      void load();
    } catch {
      setImportProblem(t("import.problem"));
    } finally {
      setImportBusy(false);
    }
  }, [api, importBatch, importPlan, importRows.length, load]);

  /* The team and the first card, the owner's only writes besides the import. Each
   * answers with the sentence the screen shows, and reloads what it changed. */
  const team: TeamActions = useMemo(() => {
    const said = (r: unknown, ok: string): string => {
      if (isRefusal(r as never)) {
        const refusal = r as { refusal: string; params?: { reason?: string; after?: string } };
        if (refusal.refusal === "E_MAIL_NOT_SENT") return t("team.mailDown");
        // Three links a day for one person (050), said with when the next can go.
        if (refusal.refusal === "E_TOO_MANY_LINKS") {
          const after = refusal.params?.after ? new Date(refusal.params.after) : null;
          if (!after || Number.isNaN(after.valueOf())) return t("team.tooManyLinksLater");
          const at = after.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
          return t("team.tooManyLinks", { at });
        }
        return t("team.add.problem", { reason: refusal.params?.reason ?? t("shell.problem") });
      }
      return ok;
    };
    const post = async (path: string, body: Record<string, unknown>) =>
      api.call<Record<string, unknown>>(path, { method: "POST", body: { business_id: business?.id, ...body } });
    return {
      async add(username: string, email: string) {
        const r = await post("/merchant/staff", { username, email });
        void load();
        if (isRefusal(r)) return { ok: false, message: said(r, "") };
        const out = r as unknown as { already_signed_in?: boolean };
        return {
          ok: true,
          message: out.already_signed_in ? t("team.add.already") : t("team.add.done", { who: email }),
        };
      },
      async resend(staffId: string) {
        const r = await post("/merchant/staff/link", { staff_id: staffId });
        return { ok: !isRefusal(r), message: said(r, t("team.resent")) };
      },
      async remove(staffId: string) {
        const r = await post("/merchant/staff/remove", { staff_id: staffId });
        void load();
        return { ok: !isRefusal(r), message: said(r, t("team.removed")) };
      },
    };
  }, [api, business?.id, load]);

  const startCard = useCallback(
    async (stamps: number, reward: string): Promise<string | null> => {
      const r = await api.call<Record<string, unknown>>("/merchant/program/create", {
        method: "POST",
        body: {
          business_id: business?.id,
          stamps_required: stamps,
          reward_description: reward,
          min_gap_minutes: FIRST_CARD_GAP_MINUTES,
        },
      });
      if (isRefusal(r)) {
        const reason = (r as { params?: { reason?: string } }).params?.reason ?? t("shell.problem");
        return t("programme.create.problem", { reason });
      }
      await load();
      return null;
    },
    [api, business?.id, load],
  );

  if (!session) {
    if (setupToken) {
      return (
        <MerchantSetup
          auth={merchantAuth}
          token={setupToken}
          words={setupWords}
          brand={
            <div className="side-brand" style={{ padding: 0 }}>
              <span className="mark" aria-hidden="true" />
              <span className="side-word">Damga</span>
            </div>
          }
          onDone={(username) => {
            setPrefill(username);
            setSetupToken(null);
          }}
        />
      );
    }
    return (
      <MerchantSignIn
        auth={merchantAuth}
        words={signInWords}
        initialUsername={prefill}
        brand={
          <div className="side-brand" style={{ padding: 0 }}>
            <span className="mark" aria-hidden="true" />
            <span className="side-word">Damga</span>
          </div>
        }
        onSignedIn={setSession}
      />
    );
  }

  const role = business?.my_role ?? "";
  const owner = role === "owner";

  /* THE COUNTER TAKES THE SCREEN. For the owner it is a screen with a way back;
   * for anyone else it is the whole dashboard, and "End shift" signs them out. */
  if (business && (tab === "counter" || (asked && !owner))) {
    return (
      <CounterScreen
        api={api}
        business={business}
        endLabel={owner ? t("counter.back") : t("counter.endShift")}
        onEnd={owner ? () => setTab("overview") : signOut}
        onSessionEnded={() => setSession(null)}
      />
    );
  }
  const body = () => {
    if (problem === "notStaff") {
      return (
        <div className="empty">
          <p className="empty-title">{t("auth.notStaff")}</p>
        </div>
      );
    }
    if (!asked) return <p className="note">{t("shell.loading")}</p>;
    switch (tab) {
      case "overview":
        return overview ? (
          <OverviewScreen
            data={overview}
            program={program}
            branches={branches ?? []}
            staff={staff ?? []}
            tills={tills ?? []}
            onCustomers={() => setTab("customers")}
          />
        ) : null;
      case "customers":
        return <CustomersScreen customers={customers ?? []} query={query} onQuery={setQuery} />;
      case "programme":
        return <ProgrammeScreen program={program} onStart={startCard} />;
      case "rewards":
        return <RewardsScreen rewards={rewards ?? []} />;
      case "branches":
        return <BranchesScreen branches={branches ?? []} />;
      case "team":
        return <TeamScreen staff={staff ?? []} actions={team} />;
      case "card":
        return <CardScreen name={business?.name ?? ""} program={program} />;
      case "import":
        return (
          <ImportScreen
            rows={importRows}
            plan={importPlan}
            busy={importBusy}
            done={importDone}
            problem={importProblem}
            onAdd={addImportRow}
            onRemove={(phone) => setImportRows((rows) => rows.filter((r) => r.phone !== phone))}
            onCheck={() => void checkImport()}
            onApply={() => void applyImport()}
            onStartOver={startOver}
          />
        );
      case "counter":
        return null;
      case "reports":
        return (
          <ExportsScreen
            onCustomers={() =>
              csv(
                (customers ?? []).map((c) => ({
                  customer: c.name ?? "",
                  shown_as: c.masked ?? "",
                  balance: c.balance,
                  threshold: c.threshold,
                  visits: c.visits,
                  rewards: c.rewards,
                  joined: c.joined ?? "",
                  last_visit: c.last_earn_at ?? "",
                  branch: c.branch ?? "",
                })),
                "customers",
              )
            }
            onRewards={() =>
              csv(
                (rewards ?? []).map((r) => ({
                  when: r.at,
                  state: r.type,
                  customer: r.who ?? "",
                  reward: r.reward ?? "",
                  branch: r.branch ?? "",
                  staff: r.staff ?? "",
                })),
                "rewards",
              )
            }
          />
        );
      case "settings":
        return (
          <SettingsScreen
            name={business?.name ?? ""}
            role={role}
            contactPhone={business?.contact_phone ?? null}
            city={business?.city ?? null}
          />
        );
    }
  };

  const title = TABS.find((x) => x.key === tab)!.label;
  const ranged = tab === "overview" || tab === "rewards";

  return (
    <div className="app">
      <aside className="side">
        {/* UNDER 900px, ONE BAR AND A MENU (review of 2026-09-18, findings 11 and
            12). It was three bars -- the brand, eleven sections in a row that
            scrolled sideways with no sign it did, then the shop's name again and
            Sign out -- before any content. Now the brand and a Menu button; the
            sections, the shop and Sign out open beneath it. The page's own
            heading already names the shop and the section. Above 900px the
            sidebar is as it was, and the button is not drawn. */}
        <div className="side-top">
          <div className="side-brand">
            <span className="mark" aria-hidden="true" />
            <span className="side-word">Damga</span>
          </div>
          <button
            type="button"
            className="side-menu"
            aria-expanded={menuOpen}
            aria-controls="side-panel"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? t("shell.menuClose") : t("shell.menu")}
          </button>
        </div>
        <div id="side-panel" className="side-panel" data-open={menuOpen ? "" : undefined}>
          <nav className="side-nav" aria-label="Sections">
            {TABS.filter((x) => owner || x.key === "counter").map((x) => (
              <button
                key={x.key}
                type="button"
                className="side-link"
                aria-current={tab === x.key ? "page" : undefined}
                onClick={() => {
                  setTab(x.key);
                  setMenuOpen(false);
                }}
              >
                {t(x.label)}
              </button>
            ))}
          </nav>
          <div className="side-foot">
            <span className="side-who">{business?.name ?? t("shell.loading")}</span>
            <button type="button" className="btn btn-quiet" onClick={signOut}>
              {t("shell.signOut")}
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="head">
          <div>
            <p className="eyebrow">{business?.name ?? ""}</p>
            <h1 className="title">{t(title)}</h1>
          </div>
          {ranged && (
            <div className="range" role="group" aria-label={t("shell.range.30")}>
              {([7, 30, 90] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={days === d}
                  onClick={() => setDays(d)}
                >
                  {t(d === 7 ? "shell.range.7" : d === 30 ? "shell.range.30" : "shell.range.90")}
                </button>
              ))}
            </div>
          )}
        </div>

        {problem === "offline" && <p className="banner">{t("shell.offline")}</p>}
        {problem === "server" && <p className="banner">{t("shell.problem")}</p>}

        {body()}
      </main>
    </div>
  );
}
